import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError, } from '@reduxjs/toolkit/query/react';
import { RootState } from './store';
import { clearCredentials, setCredentials, } from '../features/auth/auth-slice';
import { UserCredential } from '../features/auth/auth-state';
import { API_BASE_URL } from '../config/api-config';

const baseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    /** IMPORTANT
    This allows the browser to send the HTTP-only refresh-token cookie. */
    credentials: 'include',
    prepareHeaders: ( headers, { getState }) => {

        const token = (getState() as RootState).authSlice.userCredentials?.accessToken;
        if (token) {
            headers.set('authorization', `Bearer ${token}`);
        }
        return headers;
    },
});


const baseQueryWithReauth: BaseQueryFn< string | FetchArgs, unknown, FetchBaseQueryError > = async ( args, api, extraOptions ) => {

    /* First attempt the original API request. */
    let result = await baseQuery(args, api, extraOptions);

    /* Only attempt refresh when the backend explicitly tells us the access token is unauthorized. */
    if (result?.error?.status === 401) {
        /* Try to get a new access token. The refresh token should be sent automatically through the HTTP-only cookie.*/
        const refreshResult = await baseQuery({ url: '/api/user/refresh-token', method: 'POST', }, api, extraOptions);

        /* Refresh succeeded. */
        if (refreshResult?.data) {
            const { accessToken, user, } = refreshResult.data as UserCredential;

            /* Store the new access token/user in Redux. */
            api.dispatch(setCredentials({ status: true, accessToken, refreshToken: '', user, }));

            /* Retry the original API request using the new access token. */
            result = await baseQuery(args, api, extraOptions);
        } else {
            /* Refresh failed. The user's session is no longer valid. */
            api.dispatch(clearCredentials());
        }
    }

    return result;
};


export const apiSlice = createApi({
    baseQuery: baseQueryWithReauth,
    endpoints: builder => ({}),
    tagTypes: [
        'User',
        'Proposal',
        'ProposalById',
        'BookedProposal',
        'AccountCategory',
        'ProposalChats',
        'ProposalFiles',
        'ProposalExtension',
        'AcctRole',
        'AcctDepartment',
        'AcctLocation',
        'AcctReportHead',
        'Amendments',
        'ComputationApproval',
        'ComputationApprovalHistory',
        'Policy',
        'PolicyTAT',
        'RequestExistingGroup'
    ],
});
